import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Product } from "@onsemble/shared";
import { useProductQueryCache } from "./use-product-query-cache";
import { client, handleClientMethod } from "../requests/client";

/**
 * Hook for fetching initial products to populate product catalogs
 * Returns the first 50 products for initial display in new quote/order pages
 * Uses enhanced TanStack Query caching with automatic cache management
 */
export const useInitialProducts = () => {
  const { queryKeys } = useProductQueryCache();

  const {
    data: products,
    isLoading,
    error,
  } = useQuery<Product[]>({
    queryKey: queryKeys.initial(),
    queryFn: async () => {
      return await handleClientMethod(client.GET("/api/products"));
    },
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  return {
    products: products || [],
    isLoading,
    error: error as Error | null,
  };
};

/**
 * Hook for looking up specific products by their IDs
 * Used primarily for template validation and order processing
 * First checks cache, then fetches missing products from API
 */
export const useProductsLookup = (productIds: string[], enabled = true) => {
  const { queryKeys, getProductFromCache } = useProductQueryCache();

  const currentQueryKey = queryKeys.lookup(productIds);

  const {
    data: products,
    isLoading,
    error,
  } = useQuery<Product[]>({
    queryKey: currentQueryKey,
    queryFn: async () => {
      if (productIds.length === 0) return [];

      // Check cache for individual products to maintain order
      const foundProductsMap = new Map<string, Product>();
      const missingIds: string[] = [];

      productIds.forEach((id) => {
        const cachedProduct = getProductFromCache(id);
        if (cachedProduct) {
          foundProductsMap.set(id, cachedProduct);
        } else {
          missingIds.push(id);
        }
      });

      // If all products found in cache, return them in input order
      if (missingIds.length === 0) {
        return productIds
          .map((id) => foundProductsMap.get(id))
          .filter(Boolean) as Product[];
      }

      // Fetch only missing products from API
      const idsParam = missingIds.join(",");
      const response = await apiRequest(
        "GET",
        `/api/products/lookup?ids=${encodeURIComponent(idsParam)}`,
      );

      if (!response.ok) {
        throw new Error("Failed to lookup products");
      }

      const fetchedProducts = (await response.json()) as Product[];

      // Combine cached and fetched products, maintaining original order
      const allProductsMap = new Map(foundProductsMap);
      fetchedProducts.forEach((product) => {
        allProductsMap.set(product.uid, product);
      });

      // Get final products in the same order as requested IDs
      const finalProducts = productIds
        .map((id) => allProductsMap.get(id))
        .filter(Boolean) as Product[];

      return finalProducts;
    },
    enabled: enabled && productIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes - lookup results are fairly stable
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
    // Keep prior results during key changes (e.g. an id is added/removed) so
    // dependent UI like the suggestion row doesn't flash empty mid-transition.
    placeholderData: keepPreviousData,
  });

  return {
    products: products || [],
    isLoading,
    error: error as Error | null,
  };
};
